import { useState, useRef, useEffect } from 'react';

import PropTypes from 'prop-types';
import {
	BsX,
	BsChevronDown,
	BsSearch,
	BsFileText,
	BsPencil,
	BsCheck,
	BsPlus,
	BsStopCircle,
	BsShare,
	BsInfoCircle,
	BsStars,
	BsJournalText,
} from 'react-icons/bs';
import { MdMerge } from 'react-icons/md';

import Button from '#globalComponents/Button/Button';
import Card from '#globalComponents/Card/Card';
import TextInput from '#globalComponents/Forms/TextInput';
import Menu from '#globalComponents/Overlay/Menu';
import Panel from '#globalComponents/Overlay/Panel';
import Popover from '#globalComponents/Overlay/Popover';
import Tag from '#globalComponents/Tag/Tag';
import Tooltip from '#globalComponents/Tooltip/Tooltip';
import { usePreferencesStore } from '#lib/stores/preferencesStore';

import { useTargetingVocabulary } from '../../../../../models/targeting/queries';
import { formatQueryInputPlainText } from '../../../../../utils/formatQueryInput';
import { isRunningSearchJob } from '../../../../../utils/isRunningSearchJob';
import { ShareQueryModal } from '../../../../ShareQueryModal/ShareQueryModal';

import { QueryInputTooltipContent } from './QueryInputTooltip';

const ROW_ACTION_TONES = {
	default: 'hover:text-text hover:bg-bgBase-400',
	danger: 'hover:text-warning-700 hover:bg-warning-200',
};

function RowAction({ icon: Icon, title, onClick, tone = 'default', revealOnHover = true }) {
	return (
		<button
			type="button"
			title={title}
			onClick={(e) => {
				e.stopPropagation();
				onClick();
			}}
			className={`w-7 h-7 flex items-center justify-center rounded-control text-bgBase-800 transition-colors ${
				ROW_ACTION_TONES[tone]
			} ${revealOnHover ? 'opacity-0 group-hover:opacity-100' : ''}`}
		>
			<Icon className="w-4 h-4" />
		</button>
	);
}

RowAction.propTypes = {
	icon: PropTypes.elementType.isRequired,
	title: PropTypes.string.isRequired,
	onClick: PropTypes.func.isRequired,
	tone: PropTypes.oneOf(Object.keys(ROW_ACTION_TONES)),
	revealOnHover: PropTypes.bool,
};

function SharedByTag({ sharedBy, orgMembers }) {
	const sharer = orgMembers.find((m) => m.userId === sharedBy);
	const sharerName = sharer ? sharer.fullName || sharer.email : null;
	return <Tag label={sharerName ? `Geteilt von ${sharerName}` : 'Geteilt'} tone="success" size="xs" />;
}

SharedByTag.propTypes = {
	sharedBy: PropTypes.string,
	orgMembers: PropTypes.array,
};

export default function ProspectQuerySelector({
	queries,
	selected,
	onSelect,
	onDelete,
	onRename,
	onMerge,
	contactCount = 0,
	onExtend,
	onResumeAssistant,
	onShowProtocol,
	onAbort,
	runningJobs = [],
	isCancellingSearch = false,
	orgMemberCount = 1,
	orgMembers = [],
}) {
	const [isOpen, setIsOpen] = useState(false);
	const [editingId, setEditingId] = useState(null);
	const [editValue, setEditValue] = useState('');
	const [shareQuery, setShareQuery] = useState(null);
	const sharedSectionOpen = usePreferencesStore((s) => s.prospectQuerySelectorSharedSectionOpen);
	const setSharedSectionOpen = usePreferencesStore((s) => s.setProspectQuerySelectorSharedSectionOpen);
	const { data: targetingVocabulary } = useTargetingVocabulary();
	const inputRef = useRef(null);
	const triggerRef = useRef(null);

	const ownQueries = queries.filter((q) => !q.isShared);
	const sharedQueries = queries.filter((q) => q.isShared);

	const toggleSharedSection = (e) => {
		e.stopPropagation();
		setSharedSectionOpen(!sharedSectionOpen);
	};

	const closeDropdown = () => {
		setIsOpen(false);
		setEditingId(null);
	};

	// Focus input when editing starts
	useEffect(() => {
		if (editingId && inputRef.current) {
			inputRef.current.focus();
			inputRef.current.select();
		}
	}, [editingId]);

	const handleSelect = (queryId) => {
		if (editingId) return; // Don't select if we're editing
		onSelect(queryId);
		setIsOpen(false);
	};

	const startEditing = (query) => {
		setEditingId(query.id);
		setEditValue(query.queryName || '');
	};

	const handleSaveEdit = async (queryId) => {
		if (!editValue.trim()) {
			setEditingId(null);
			return;
		}

		if (onRename) {
			await onRename(queryId, editValue.trim());
		}
		setEditingId(null);
	};

	const handleKeyDown = (e, queryId) => {
		if (e.key === 'Enter') {
			e.preventDefault();
			handleSaveEdit(queryId);
		} else if (e.key === 'Escape') {
			setEditingId(null);
		}
	};

	// Check if there's a running job (Google Maps or OpenRegister) for the selected query
	const runningJob = runningJobs.find(isRunningSearchJob);
	const hasRunningJob = Boolean(runningJob);

	// A query can be extended if it has results (via companyCount or contactCount prop) or Apify data
	// CSV imports have no search parameters to continue; an agent list is extended by a new run.
	const isExtendableQueryType = !['csvImport', 'agentCuratedSearch'].includes(selected?.queryTypes?.code);
	const isAgentQuery = selected?.queryTypes?.code === 'agentCuratedSearch' && !selected?.isShared;
	const canExtendQuery =
		selected &&
		!selected.isShared &&
		isExtendableQueryType &&
		(contactCount > 0 || selected.apifyDatasetId || selected.apifyRunId);

	const renderQueryRow = (q) => {
		const isSelected = selected?.id === q.id;
		const queryResultCount = isSelected ? contactCount : (q.companyCount ?? 0);
		const isEditing = editingId === q.id;

		return (
			<div
				key={q.id}
				className={`group flex items-center gap-2 px-3.5 py-2 text-sm transition-colors ${
					isSelected || isEditing ? 'bg-primary-100 text-primary-700' : 'text-text hover:bg-bgBase-300'
				} ${isEditing ? '' : 'cursor-pointer'}`}
				onClick={() => !isEditing && handleSelect(q.id)}
			>
				<span className="flex-shrink-0">
					<Tag label={String(queryResultCount)} tone={isSelected ? 'primary' : 'neutral'} />
				</span>
				{isEditing ? (
					<TextInput
						ref={inputRef}
						value={editValue}
						onChange={(e) => setEditValue(e.target.value)}
						onKeyDown={(e) => handleKeyDown(e, q.id)}
						className="flex-1"
						onClick={(e) => e.stopPropagation()}
					/>
				) : (
					<div className="flex-1 min-w-0" onDoubleClick={() => !q.isShared && onRename && startEditing(q)}>
						<div className="flex items-center gap-1.5 min-w-0">
							<span
								className="truncate"
								title={
									q.input
										? formatQueryInputPlainText(q.input, q.queryTypes?.code, targetingVocabulary)
										: undefined
								}
							>
								{q.queryName || 'Unbekannter Eintrag'}
							</span>
							{q.isShared && <SharedByTag sharedBy={q.sharedBy} orgMembers={orgMembers} />}
						</div>
						{q.isShared && (
							<p className="text-[10px] text-bgBase-800 mt-0.5 leading-tight">
								Nur lesen – Erweitern &amp; Zusammenführen nicht verfügbar
							</p>
						)}
					</div>
				)}
				{!q.isShared && (
					<div className="flex items-center gap-0.5 flex-shrink-0">
						{isEditing ? (
							<RowAction
								icon={BsCheck}
								title="Änderungen speichern"
								onClick={() => handleSaveEdit(q.id)}
								revealOnHover={false}
							/>
						) : (
							<>
								{onRename && (
									<RowAction
										icon={BsPencil}
										title="Namen bearbeiten"
										onClick={() => startEditing(q)}
									/>
								)}
								{orgMemberCount > 1 && (
									<RowAction
										icon={BsShare}
										title="Suchanfrage teilen"
										onClick={() => {
											setShareQuery(q);
											setIsOpen(false);
										}}
									/>
								)}
								{onMerge && ownQueries.length > 1 && (
									<RowAction
										icon={MdMerge}
										title="Mit anderer Liste zusammenführen"
										onClick={() => {
											setIsOpen(false);
											onMerge(q);
										}}
									/>
								)}
								<RowAction
									icon={BsX}
									title="Suchanfrage löschen"
									onClick={() => {
										setIsOpen(false);
										onDelete(q.id);
									}}
									tone="danger"
								/>
							</>
						)}
					</div>
				)}
			</div>
		);
	};

	const trigger = (
		<button
			ref={triggerRef}
			type="button"
			onClick={() => setIsOpen(!isOpen)}
			className="flex min-w-0 items-center gap-3 px-2 py-1.5 text-left"
		>
			<BsSearch className="w-4 h-4 text-bgBase-800 flex-shrink-0" />
			<span className="truncate text-md font-semibold text-text">
				{selected?.queryName || 'Suchanfrage wählen'}
			</span>
			{selected?.input && Object.keys(selected.input).length > 0 && (
				<Tooltip
					content={
						<QueryInputTooltipContent input={selected.input} queryTypeCode={selected.queryTypes?.code} />
					}
					position="bottom-left"
				>
					<span
						className="flex-shrink-0 text-bgBase-800 hover:text-primary-600 transition-colors cursor-help"
						onClick={(e) => e.stopPropagation()}
					>
						<BsInfoCircle className="w-3.5 h-3.5" />
					</span>
				</Tooltip>
			)}
			{selected && (
				<span className="flex-shrink-0">
					<Tag label={String(contactCount)} />
				</span>
			)}
			<BsChevronDown
				className={`w-4 h-4 text-bgBase-800 transition-transform duration-200 flex-shrink-0 ${
					isOpen ? 'rotate-180' : ''
				}`}
			/>
		</button>
	);

	return (
		<Card data-tour="query-selector" className="flex min-w-0 items-center gap-3 p-2">
			{trigger}
			<Popover open={isOpen} onClose={closeDropdown} anchorRef={triggerRef} size="l">
				<Panel.Header icon={BsFileText}>Gespeicherte Suchanfragen</Panel.Header>

				<Panel.Body padded={false}>
					{ownQueries.length === 0 && sharedQueries.length === 0 && (
						<Menu.Empty>Keine Suchanfragen vorhanden</Menu.Empty>
					)}

					{ownQueries.length > 0 && <Menu>{ownQueries.map(renderQueryRow)}</Menu>}

					{sharedQueries.length > 0 && (
						<>
							{ownQueries.length > 0 && <Menu.Separator />}
							<button
								type="button"
								onClick={toggleSharedSection}
								className="w-full flex items-center justify-between px-3.5 py-1.5 text-xs-head text-bgBase-800 hover:text-text transition-colors"
							>
								<span>Mit mir geteilt ({sharedQueries.length})</span>
								<BsChevronDown
									className={`w-3.5 h-3.5 transition-transform duration-200 ${
										sharedSectionOpen ? 'rotate-180' : ''
									}`}
								/>
							</button>
							{sharedSectionOpen && <Menu>{sharedQueries.map(renderQueryRow)}</Menu>}
						</>
					)}
				</Panel.Body>
			</Popover>

			{selected && (
				<>
					{!hasRunningJob && !isCancellingSearch && onExtend && canExtendQuery && (
						<Button
							onClick={() => onExtend(selected)}
							title="Suche erweitern - Weitere Unternehmen finden"
							variant="outline"
							className="flex-shrink-0"
						>
							<BsPlus className="w-5 h-5" />
							<span className="hidden sm:inline">Suche erweitern</span>
						</Button>
					)}

					{isAgentQuery && onResumeAssistant && (
						<Button
							onClick={() => onResumeAssistant(selected)}
							title="Gespräch mit dem Assistenten fortsetzen"
							variant="outline"
							className="flex-shrink-0"
						>
							<BsStars className="w-4 h-4" />
							<span className="hidden sm:inline">Mit Assistent fortsetzen</span>
						</Button>
					)}
					{isAgentQuery && onShowProtocol && (
						<Button
							onClick={() => onShowProtocol(selected)}
							title="Protokoll des Assistenten: Tool-Aufrufe und Kosten"
							variant="outline"
							className="flex-shrink-0"
						>
							<BsJournalText className="w-4 h-4" />
							<span className="hidden sm:inline">Protokoll</span>
						</Button>
					)}

					{(hasRunningJob || isCancellingSearch) && onAbort && (
						<Button
							onClick={() => runningJob && onAbort(runningJob.bullJobId, runningJob.jobType)}
							disabled={isCancellingSearch}
							title={isCancellingSearch ? 'Suche wird abgebrochen...' : 'Laufende Suche abbrechen'}
							variant="danger"
							className="flex-shrink-0"
						>
							<BsStopCircle className={`w-4 h-4 ${isCancellingSearch ? 'animate-spin' : ''}`} />
							<span className="hidden sm:inline">
								{isCancellingSearch ? 'Wird abgebrochen...' : 'Abbrechen'}
							</span>
						</Button>
					)}
				</>
			)}

			<ShareQueryModal show={Boolean(shareQuery)} onClose={() => setShareQuery(null)} query={shareQuery} />
		</Card>
	);
}

ProspectQuerySelector.propTypes = {
	queries: PropTypes.array,
	selected: PropTypes.object,
	onSelect: PropTypes.func,
	onDelete: PropTypes.func,
	onRename: PropTypes.func,
	onMerge: PropTypes.func,
	contactCount: PropTypes.number,
	onExtend: PropTypes.func,
	onResumeAssistant: PropTypes.func,
	onShowProtocol: PropTypes.func,
	onAbort: PropTypes.func,
	runningJobs: PropTypes.array,
	isCancellingSearch: PropTypes.bool,
	orgMemberCount: PropTypes.number,
	orgMembers: PropTypes.array,
};
